
import axios from 'axios';

export const getBaseURL = (): string => {
  return import.meta.env.VITE_API_URL || '/api/v1';
};

export const BASE_URL = getBaseURL();

interface User {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  role?: string;
  has_password?: boolean;
  preferred_currency: string;
  avatar_url?: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface LoginResponseData {
  user: User;
  token: string;
  refresh_token: string;
}

interface LoginResponse {
  success: boolean;
  message: string;
  data: LoginResponseData;
  timestamp: string;
}

const axiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: false,
});

// Auth data is written to sessionStorage by default (cleared when the browser/tab closes)
// and only promoted to localStorage (persists across restarts) when "Remember me" is checked.
// Reads check sessionStorage first, then fall back to localStorage, so both modes work
// transparently everywhere else in the app.
const AUTH_KEYS = ['jwt', 'refresh_token', 'isLoggedIn', 'user', 'email', 'userRole', 'userId', 'userProfile', 'tekjuice_sales', 'permissions', 'organization', 'organizations', 'isPlatformAdmin'] as const;

// The active organization is a device preference (kept across sign-ins); the server checks it
// against the user's memberships on every request, so it is never trusted on its own.
const ORG_KEY = 'organization_id';
export const getOrganizationId = (): string | null => localStorage.getItem(ORG_KEY);
export const setOrganizationId = (id: string | null): void => {
  if (id) localStorage.setItem(ORG_KEY, id);
  else localStorage.removeItem(ORG_KEY);
};
const rememberMode = (): boolean => localStorage.getItem('refresh_token') !== null;

const getAuthItem = (key: string): string | null => {
  return sessionStorage.getItem(key) ?? localStorage.getItem(key);
};

const setAuthItem = (key: string, value: string, remember: boolean): void => {
  sessionStorage.removeItem(key);
  localStorage.removeItem(key);
  (remember ? localStorage : sessionStorage).setItem(key, value);
};

const removeAuthItem = (key: string): void => {
  sessionStorage.removeItem(key);
  localStorage.removeItem(key);
};

const logout = () => {
  const refresh = getAuthItem('refresh_token');
  const access = getAuthItem('jwt');
  if (refresh && access) {
    // Best effort: revoke the refresh token server-side so it cannot be reused.
    axios.post(`${BASE_URL}/auth/logout`, { refresh_token: refresh }, { headers: { Authorization: `Bearer ${access}` } }).catch(() => {});
  }
  AUTH_KEYS.forEach(removeAuthItem);

  window.location.href = '/login';

  if ('Notification' in window && Notification.permission === 'granted') {
    new Notification('Session Expired', {
      body: 'You have been logged out. Please log in again.',
      icon: '/logo.png'
    });
  }
};

export const storeAuthData = (loginResponse: LoginResponse, remember: boolean = true) => {
  if (loginResponse.success && loginResponse.data) {
    const { user, token, refresh_token } = loginResponse.data;

    setAuthItem('jwt', token, remember);
    setAuthItem('refresh_token', refresh_token, remember);
    setAuthItem('isLoggedIn', 'true', remember);
    setAuthItem('user', JSON.stringify(user), remember);
    setAuthItem('email', user.email, remember);
    setAuthItem('userId', user.id, remember);

    return true;
  }
  return false;
};

export const getStoredUser = (): User | null => {
  try {
    const userStr = getAuthItem('user');
    return userStr ? JSON.parse(userStr) : null;
  } catch (error) {
    console.error('Error parsing stored user data:', error);
    return null;
  }
};

export const isLoggedIn = (): boolean => {
  const token = getAuthItem('jwt');
  const loggedIn = getAuthItem('isLoggedIn');
  return !!(token && loggedIn === 'true');
};

export const getAuthToken = (): string | null => {
  return getAuthItem('jwt');
};

export const getRefreshToken = (): string | null => {
  return getAuthItem('refresh_token');
};

export const getStoredValue = (key: string): string | null => {
  return getAuthItem(key);
};

// One refresh at a time: concurrent 401s wait for the same rotation.
let refreshing: Promise<boolean> | null = null;
const rotateTokens = (): Promise<boolean> => {
  refreshing ??= (async () => {
    try {
      const res = await axios.post(`${BASE_URL}/auth/refresh`, { refresh_token: getRefreshToken() }, { headers: { Accept: 'application/json' } });
      const remember = rememberMode();
      setAuthItem('jwt', res.data.data.token, remember);
      setAuthItem('refresh_token', res.data.data.refresh_token, remember);
      return true;
    } catch {
      return false;
    }
  })().finally(() => { refreshing = null; });
  return refreshing;
};

// When the remembered organization no longer exists or the user left it (e.g. after the database
// was rebuilt), forget it and pick one of the user's real memberships. Runs once at a time.
let recoveringOrganization: Promise<boolean> | null = null;
const recoverOrganization = (): Promise<boolean> => {
  if (recoveringOrganization) return recoveringOrganization;
  recoveringOrganization = (async () => {
    try {
      setOrganizationId(null);
      return (await loadSession()).hasOrganization;
    } catch {
      return false;
    }
  })().finally(() => { recoveringOrganization = null; });
  return recoveringOrganization;
};

axiosInstance.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    const organizationId = getOrganizationId();
    if (organizationId && !config.headers['X-Organization-Id']) {
      config.headers['X-Organization-Id'] = organizationId;
    }
    
    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    if (error.response) {
      if (error.response.status === 0) {
        console.error('CORS/Network error: Request was blocked');
        throw new Error('Network error: Unable to connect to server. Please check your connection and try again.');
      }
      
      // Expired access token: rotate the refresh token once and replay the request.
      // 403 means "not allowed" (permission / organization) and never ends the session.
      const original = error.config || {};
      const isAuthCall = typeof original.url === 'string' && (original.url.includes('/auth/login') || original.url.includes('/auth/refresh'));
      if (error.response.status === 401 && !isAuthCall) {
        if (!original._retried && getRefreshToken()) {
          original._retried = true;
          if (await rotateTokens()) {
            return axiosInstance(original);
          }
        }
        const currentPath = window.location.pathname;
        if (!currentPath.includes('/login')) {
          logout();
        }
      }

      // "You are not a member of this organization": the remembered organization is stale.
      // Switch to a real membership once and replay the request with the new organization.
      // (Requests that arrive while a repair is running wait for it. /organization itself is the
      // session loader's own call, which handles this case without the interceptor.)
      const url = String(original.url ?? '');
      if (error.response.status === 403 && error.response.data?.errors?.code === 'no_organization'
        && !original._orgRetried && !/\/organization$/.test(url.split('?')[0]) && getAuthToken()) {
        original._orgRetried = true;
        if (await recoverOrganization()) {
          if (original.headers) delete original.headers['X-Organization-Id'];
          return axiosInstance(original);
        }
      }

      if (error.response.status >= 500) {
        console.error('Server error:', error.response.status);
        throw new Error('Server error: Please try again later.');
      }
    } else if (error.request) {
      console.error('Network error: No response received');
      throw new Error('Network error: Please check your internet connection.');
    }
    
    return Promise.reject(error);
  }
);

export const get = async <T>(url: string, params?: object): Promise<T> => {
  try {
    const response = await axiosInstance.get<T>(url, { params });
    return response.data;
  } catch (error) {
    console.error(`GET ${url} failed:`, error);
    throw error;
  }
};

export const post = async <T>(url: string, data?: object): Promise<T> => {
  try {
    const response = await axiosInstance.post<T>(url, data);
    return response.data;
  } catch (error) {
    console.error(`POST ${url} failed:`, error);
    throw error;
  }
};

export const put = async <T>(url: string, data?: object): Promise<T> => {
  try {
    const response = await axiosInstance.put<T>(url, data);
    return response.data;
  } catch (error) {
    console.error(`PUT ${url} failed:`, error);
    throw error;
  }
};

export const del = async <T>(url: string): Promise<T> => {
  try {
    const response = await axiosInstance.delete<T>(url);
    return response.data;
  } catch (error) {
    console.error(`DELETE ${url} failed:`, error);
    throw error;
  }
};

export const upload = async <T>(url: string, formData: FormData): Promise<T> => {
  try {
    const response = await axiosInstance.post<T>(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error(`UPLOAD ${url} failed:`, error);
    throw error;
  }
};

export const downloadFile = async (url: string, filename: string): Promise<void> => {
  try {
    const response = await axiosInstance.get(url, { responseType: 'blob' });
    const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error(`DOWNLOAD ${url} failed:`, error);
    throw error;
  }
};

/** Fetch a protected file as a Blob (uses the auth header, so it works for private storage). */
export const fetchBlob = async (url: string): Promise<Blob> => {
  const response = await axiosInstance.get(url, { responseType: 'blob' });
  return response.data as Blob;
};

export const patch = async <T>(url: string, data?: object): Promise<T> => {
  try {
    const response = await axiosInstance.patch<T>(url, data);
    return response.data;
  } catch (error) {
    console.error(`PATCH ${url} failed:`, error);
    throw error;
  }
};

// Auth-specific methods
export const login = async (email: string, password: string): Promise<LoginResponse> => {
  try {
    const response = await post<LoginResponse>('/auth/login', { email, password });
    
    if (response.success) {
      storeAuthData(response);
    }
    
    return response;
  } catch (error) {
    console.error('Login failed:', error);
    throw error;
  }
};

export const refreshToken = async (): Promise<LoginResponse> => {
  try {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }
    
    const response = await post<LoginResponse>('/auth/refresh', { refresh_token: refreshToken });
    
    if (response.success) {
      storeAuthData(response);
    }
    
    return response;
  } catch (error) {
    console.error('Token refresh failed:', error);
    logout();
    throw error;
  }
};

export const validateToken = async (): Promise<boolean> => {
  try {
    const token = getAuthToken();
    if (!token) {
      return false;
    }
    
    await get('/auth/me');
    return true;
  } catch (error) {
    console.error('Token validation failed:', error);
    return false;
  }
};

export interface OrganizationSummary {
  id: string; name: string; slug: string; status: string; membership_status: string;
  role: { key: string | null; name: string | null };
}

/**
 * Loads who the user is, which organizations they belong to, and their permissions in the
 * active one. Called after sign-in and on app start; the sidebar and pages read the result.
 */
export const loadSession = async (): Promise<{ hasOrganization: boolean; platformAdmin: boolean }> => {
  const remember = rememberMode();
  const me = (await get<{ data: { user: User; is_platform_admin: boolean; organizations: OrganizationSummary[]; current_organization_id: string | null } }>('/auth/me')).data;
  setAuthItem('user', JSON.stringify(me.user), remember);
  setAuthItem('email', me.user.email, remember);
  setAuthItem('userId', me.user.id, remember);
  setAuthItem('isPlatformAdmin', me.is_platform_admin ? '1' : '0', remember);
  setAuthItem('organizations', JSON.stringify(me.organizations), remember);

  // System administrators run the platform and have no organization (and no organization data).
  if (me.is_platform_admin) {
    setOrganizationId(null);
    setAuthItem('organization', '', remember);
    setAuthItem('permissions', '[]', remember);
    setAuthItem('userRole', 'System Administrator', remember);
    window.dispatchEvent(new Event('session-changed'));
    return { hasOrganization: false, platformAdmin: true };
  }

  const usable = me.organizations.filter((o) => o.membership_status === 'active' && o.status === 'active');
  const stored = getOrganizationId();
  const chosen = usable.find((o) => o.id === stored) ?? usable.find((o) => o.id === me.current_organization_id) ?? usable[0];
  if (!chosen) {
    setOrganizationId(null);
    setAuthItem('permissions', '[]', remember);
    return { hasOrganization: false, platformAdmin: false };
  }
  setOrganizationId(chosen.id);
  const ctx = (await get<{ data: { organization: { id: string; name: string; base_currency: string }; role: { key: string; name: string } | null; permissions: string[] } }>('/organization')).data;
  setAuthItem('organization', JSON.stringify(ctx.organization), remember);
  setAuthItem('permissions', JSON.stringify(ctx.permissions), remember);
  setAuthItem('userRole', ctx.role?.name ?? '', remember);
  window.dispatchEvent(new Event('session-changed'));
  return { hasOrganization: true, platformAdmin: false };
};

export const switchOrganization = async (id: string): Promise<void> => {
  const previous = getOrganizationId();
  setOrganizationId(id);
  try {
    await loadSession();
  } catch (e) {
    setOrganizationId(previous);
    throw e;
  }
};

export const getPermissions = (): string[] => {
  try { return JSON.parse(getAuthItem('permissions') || '[]'); } catch { return []; }
};

/** True when the user holds any of the given permissions in the active organization. */
export const can = (...permissions: string[]): boolean => {
  const held = new Set(getPermissions());
  return permissions.some((p) => held.has(p));
};

export const isPlatformAdmin = (): boolean => getAuthItem('isPlatformAdmin') === '1';

/** True while a system administrator is viewing an organization they are not a member of. */

/** Name of the user's role in the active organization (empty when unknown). */
export const getRoleName = (): string => getAuthItem('userRole') ?? '';

export const getOrganizations = (): OrganizationSummary[] => {
  try { return JSON.parse(getAuthItem('organizations') || '[]'); } catch { return []; }
};

export const getCurrentOrganization = (): { id: string; name: string; base_currency: string; logo_url?: string | null } | null => {
  try { return JSON.parse(getAuthItem('organization') || 'null'); } catch { return null; }
};

/** Keep the stored profile in step after the user edits it (name, picture), and refresh the layout. */
export const updateStoredUser = (user: Partial<User>): void => {
  const current = getStoredUser();
  setAuthItem('user', JSON.stringify({ ...(current ?? {}), ...user }), rememberMode());
  window.dispatchEvent(new Event('session-changed'));
};

/** Same for the active organization (name, logo). */
export const updateStoredOrganization = (organization: { id: string; name: string; base_currency: string; logo_url?: string | null }): void => {
  if (getCurrentOrganization()?.id !== organization.id) return;
  setAuthItem('organization', JSON.stringify(organization), rememberMode());
  window.dispatchEvent(new Event('session-changed'));
};

/** Store tokens from any sign-in flow (password, OAuth exchange, invitation) and load the session. */
export const completeSignIn = async (tokens: { token: string; refresh_token: string }, remember = true) => {
  setAuthItem('jwt', tokens.token, remember);
  setAuthItem('refresh_token', tokens.refresh_token, remember);
  setAuthItem('isLoggedIn', 'true', remember);
  return loadSession();
};

export { logout }